import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  UserPlus, Mail, Phone, Briefcase, KeyRound, Crown, Trash2, Pencil, Save, X,
  Send, Copy, ShieldCheck, RefreshCw, EyeOff,
} from "lucide-react";
import AvatarUpload from "@/components/AvatarUpload";
import AgencyRepsSection from "@/components/AgencyRepsSection";
import { copyToClipboard } from "@/lib/clipboard";
import BrandedLoginCard from "@/components/contacts/BrandedLoginCard";

export default function ContactsPage() {
  const { clientId } = useParams();
  const [contacts, setContacts] = useState([]);
  const [client, setClient] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [contactsRes, clientRes] = await Promise.all([
        api.get(`/clients/${clientId}/contacts`),
        api.get(`/clients/${clientId}`),
      ]);
      setContacts(contactsRes.data || []);
      setClient(clientRes.data || null);
    } catch (e) {
      setContacts([]);
      setClient(null);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Branded login URL + hero image upload now live in <BrandedLoginCard/>.

  const setPrimary = async (c) => {
    if (c.is_primary) return;
    try {
      await api.patch(`/clients/${clientId}/contacts/${c.user_id}`, { is_primary: true });
      toast.success(`${c.name} is now the primary contact`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const uploadAvatar = async (c, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/clients/${clientId}/contacts/${c.user_id}/avatar`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    load();
  };

  const clearAvatar = async (c) => {
    await api.delete(`/clients/${clientId}/contacts/${c.user_id}/avatar`);
    load();
  };

  // heroInputRef + helpers moved to <BrandedLoginCard/>

  const [resetLinkInfo, setResetLinkInfo] = useState(null);
  const generateResetLink = async (c) => {
    try {
      const { data } = await api.post(`/users/${c.user_id}/password-reset-link`);
      setResetLinkInfo({
        url: data.reset_url, email: data.email,
        expires_at: data.expires_at, name: c.name,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate link");
    }
  };

  // Admin-set password flow — generate a strong password server-side, show it
  // ONCE so the admin can pass it to the contact via a secure channel. After
  // the dialog closes the plaintext is gone forever; only the bcrypt hash is
  // stored. The "Password set on…" timestamp on the contact row tells the
  // admin a credential exists without revealing the value.
  const [setPwInfo, setSetPwInfo] = useState(null);
  const [settingPwId, setSettingPwId] = useState(null);
  const adminResetPassword = async (c) => {
    setSettingPwId(c.user_id);
    try {
      const { data } = await api.post(`/users/${c.user_id}/admin-reset-password`);
      setSetPwInfo({
        user_id: c.user_id,
        name: c.name,
        email: data.email,
        password: data.password,
      });
      // Refresh so the row picks up the new password_set_at stamp.
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSettingPwId(null);
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const resendWelcome = async (c) => {
    setResendingId(c.user_id);
    try {
      const { data } = await api.post(`/users/${c.user_id}/send-welcome-email`);
      if (data.sent) {
        toast.success(`Welcome email sent to ${data.email}`);
      } else if (data.reset_url) {
        // SendGrid isn't wired (or transient send error) — surface both URLs in
        // the existing dialog so the admin can copy-paste them manually.
        setResetLinkInfo({
          url: data.reset_url, email: data.email,
          expires_at: data.expires_at, name: c.name,
          headline: "SendGrid not configured · copy the welcome links manually",
          loginUrl: data.login_url,
        });
        toast.info("SendGrid not configured — copy the links manually");
      } else {
        toast.error(data.reason || "Failed to send welcome email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send welcome email");
    } finally {
      setResendingId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        const payload = {};
        ["email", "first_name", "last_name", "title", "phone", "persona"].forEach((k) => {
          if (draft[k] !== undefined) payload[k] = draft[k];
        });
        if (draft.social_links !== undefined) payload.social_links = draft.social_links;
        if (draft.can_use_ai_studio !== undefined) payload.can_use_ai_studio = !!draft.can_use_ai_studio;
        if (payload.email) payload.email = payload.email.trim().toLowerCase();
        await api.patch(`/clients/${clientId}/contacts/${editingId}`, payload);
        toast.success("Contact updated");
      } else {
        const payload = {
          email: draft.email.trim().toLowerCase(),
          first_name: draft.first_name.trim(),
          last_name: draft.last_name.trim(),
          title: draft.title || null,
          phone: draft.phone || null,
          is_primary: !!draft.is_primary,
          can_login: !!draft.can_login,
        };
        if (draft.can_login) payload.password = draft.password;
        await api.post(`/clients/${clientId}/contacts`, payload);
        toast.success(draft.can_login ? "Contact invited" : "Contact added (no portal access)");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const cancel = () => {
    setAdding(false); setEditingId(null); setDraft(emptyDraft());
  };

  const beginEdit = (c) => {
    setAdding(false);
    setEditingId(c.user_id);
    setDraft({
      ...emptyDraft(),
      email: c.email,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      title: c.title || "", phone: c.phone || "",
      is_primary: !!c.is_primary,
      can_use_ai_studio: !!c.can_use_ai_studio,
      persona: c.persona || "",
      social_links: c.social_links || {},
    });
  };

  const remove = async (c) => {
    try {
      await api.delete(`/clients/${clientId}/contacts/${c.user_id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Team &amp; access</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Client contacts</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Everyone here can sign in to the client portal to view their company&apos;s
            reports. Pick the <strong>primary contact</strong> — they appear at the
            top of the PDF report; the rest are listed beneath them.
          </p>
        </div>
        <Button
          onClick={() => { setAdding(true); setEditingId(null); setDraft(emptyDraft()); }}
          className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-10"
          data-testid="contact-add-button">
          <UserPlus className="h-4 w-4 mr-1.5" /> Invite contact
        </Button>
      </div>

      {/* Branded login URL — copy & share with client contacts */}
      <BrandedLoginCard client={client} clientId={clientId} onChanged={load} />

      {(adding || editingId) && (
        <Card className="rounded-sm border-[#002FA7]/30 bg-[#F4F5FB]" data-testid="contact-edit-card">
          <CardContent className="p-5 space-y-3">
            <div>
              <div className="label-overline">{editingId ? "Edit contact" : "Invite new contact"}</div>
              <h3 className="font-display font-bold text-lg tracking-tight mt-1">
                {editingId ? "Update details" : "Send portal access to a team member"}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="First name" required>
                <Input value={draft.first_name}
                  onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
                  placeholder="e.g. Alex"
                  className="rounded-sm h-10 bg-white"
                  data-testid="contact-first-name-input" required />
              </Field>
              <Field label="Last name">
                <Input value={draft.last_name}
                  onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
                  placeholder="e.g. Smith"
                  className="rounded-sm h-10 bg-white"
                  data-testid="contact-last-name-input" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder="alex@company.com"
                  className="rounded-sm h-10 bg-white"
                  data-testid="contact-email-input" required />
              </Field>
              <Field label="Job title">
                <Input value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Marketing Manager"
                  className="rounded-sm h-10 bg-white"
                  data-testid="contact-title-input" />
              </Field>
              <Field label="Phone">
                <Input value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                  className="rounded-sm h-10 bg-white"
                  data-testid="contact-phone-input" />
              </Field>
              {!editingId && draft.can_login && (
                <Field label="Initial password" required>
                  <Input type="text" value={draft.password}
                    onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                    placeholder="≥ 8 characters"
                    className="rounded-sm h-10 bg-white font-mono"
                    data-testid="contact-password-input" required />
                </Field>
              )}
              {!editingId && (
                <Field label="Portal access">
                  <label className="flex items-center gap-2 text-sm h-10 cursor-pointer select-none">
                    <input type="checkbox"
                      checked={!draft.can_login}
                      onChange={(e) => setDraft({ ...draft, can_login: !e.target.checked, password: e.target.checked ? "" : draft.password })}
                      className="h-4 w-4 accent-[#002FA7]"
                      data-testid="contact-no-login-checkbox" />
                    <span>Directory only — <strong>no portal login</strong>, no invite</span>
                  </label>
                </Field>
              )}
              {!editingId && (
                <Field label="">
                  <label className="flex items-center gap-2 text-sm h-10 cursor-pointer select-none">
                    <input type="checkbox"
                      checked={!!draft.is_primary}
                      onChange={(e) => setDraft({ ...draft, is_primary: e.target.checked })}
                      className="h-4 w-4 accent-[#002FA7]"
                      data-testid="contact-primary-checkbox" />
                    <span>Make this the <strong>primary</strong> contact</span>
                  </label>
                </Field>
              )}
            </div>

            {/* Persona + Social Profiles — only meaningful when editing. */}
            {editingId && (
              <div className="space-y-3 pt-4 border-t border-zinc-200">
                <div className="rounded-sm border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-3">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input type="checkbox"
                      checked={!!draft.can_use_ai_studio}
                      onChange={(e) => setDraft({ ...draft, can_use_ai_studio: e.target.checked })}
                      className="h-4 w-4 accent-[#002FA7] mt-0.5"
                      data-testid="contact-ai-studio-checkbox" />
                    <div className="text-sm">
                      <strong>AI Studio + Calendar editor</strong>
                      <p className="text-[11px] text-zinc-600 mt-0.5">
                        Lets this contact create / edit calendar activities and
                        use the &quot;Create with AI&quot; wizard (copy, image, video).
                        Costs are billed to the agency&apos;s Emergent Universal Key
                        balance — enable only for senior contacts who manage content.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="label-overline">
                      AI Persona <span className="text-zinc-400 normal-case font-normal">— internal, never shown to the contact</span>
                    </Label>
                    <span className="text-[10px] font-mono text-zinc-400">
                      {(draft.persona || "").length} / 20,000
                    </span>
                  </div>
                  <textarea
                    value={draft.persona || ""}
                    onChange={(e) => setDraft({ ...draft, persona: e.target.value })}
                    rows={8}
                    maxLength={20000}
                    className="w-full rounded-sm border border-zinc-200 bg-white p-2 text-xs leading-relaxed
                      focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                    placeholder="e.g. CFO mindset — transactional, accounting-focused, cost-cutting bias. Reframe marketing as capped-downside experiments expressed in EBIT impact. Cite payback periods, break-even thresholds and incremental contribution rather than 'brand', 'creativity' or 'awareness'…"
                    data-testid="contact-persona-input" />
                  <p className="text-[11px] text-zinc-500 mt-1">
                    Helps the platform&apos;s AI assistants (text + voice) match this person&apos;s communication style.
                    The AI will silently calibrate tone and vocabulary — it will <strong>never quote</strong> the persona back.
                  </p>
                </div>

                <div>
                  <Label className="label-overline mb-1">Social profiles & websites</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      { key: "linkedin",  label: "LinkedIn",  placeholder: "https://linkedin.com/in/…" },
                      { key: "facebook",  label: "Facebook",  placeholder: "https://facebook.com/…" },
                      { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/…" },
                      { key: "x_twitter", label: "X (Twitter)", placeholder: "https://x.com/…" },
                      { key: "tiktok",    label: "TikTok",    placeholder: "https://tiktok.com/@…" },
                      { key: "youtube",   label: "YouTube",   placeholder: "https://youtube.com/@…" },
                      { key: "threads",   label: "Threads",   placeholder: "https://threads.net/@…" },
                      { key: "website",   label: "Personal website", placeholder: "https://example.com" },
                    ].map((p) => (
                      <div key={p.key}>
                        <Label className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                          {p.label}
                        </Label>
                        <Input
                          value={(draft.social_links || {})[p.key] || ""}
                          onChange={(e) => setDraft({
                            ...draft,
                            social_links: { ...(draft.social_links || {}), [p.key]: e.target.value },
                          })}
                          placeholder={p.placeholder}
                          className="rounded-sm h-9 bg-white text-xs font-mono mt-0.5"
                          data-testid={`contact-social-${p.key}`} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    These public URLs are passed to the AI as additional context. The AI may follow them
                    (or use them as a hint) to refine its register, but will not quote them back.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={submit} className="flex items-center gap-2 pt-3 border-t border-zinc-200">
              <Button type="submit"
                disabled={busy || !draft.first_name || !draft.email || (!editingId && draft.can_login && (!draft.password || draft.password.length < 8))}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="contact-save-button">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {busy ? "Saving…" : editingId ? "Save changes" : draft.can_login ? "Send invite" : "Add contact (no invite)"}
              </Button>
              <Button type="button" variant="ghost" className="rounded-sm"
                onClick={cancel} data-testid="contact-cancel-button">
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {contacts.map((c) => (
          <Card key={c.user_id}
            className={`rounded-sm transition-colors ${c.is_primary ? "border-[#002FA7]/40 bg-[#F4F5FB]" : "border-zinc-200"}`}
            data-testid={`contact-row-${c.user_id}`}>
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none"
                title="Set as primary contact"
                data-testid={`contact-primary-radio-${c.user_id}`}>
                <input
                  type="radio"
                  name="primary-contact"
                  checked={!!c.is_primary}
                  onChange={() => setPrimary(c)}
                  className="h-4 w-4 accent-[#002FA7] cursor-pointer"
                />
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                  {c.is_primary ? "Primary" : "Set primary"}
                </span>
              </label>

              <div className="flex items-center gap-3 flex-1 min-w-0">
                <AvatarUpload
                  url={c.avatar_url}
                  initials={`${(c.first_name||c.name||"?")[0]||"?"}${(c.last_name||"")[0]||""}`}
                  onUpload={(file) => uploadAvatar(c, file)}
                  onClear={c.avatar_url ? () => clearAvatar(c) : null}
                  size={44}
                  testid={`contact-avatar-${c.user_id}`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm">{c.name}</span>
                    {c.is_primary && (
                      <Badge variant="outline" className="rounded-sm text-[10px] border-[#002FA7] text-[#002FA7] bg-white">
                        <Crown className="h-2.5 w-2.5 mr-1" /> Primary
                      </Badge>
                    )}
                    {c.title && (
                      <span className="inline-flex items-center text-[11px] text-zinc-600 gap-1">
                        <Briefcase className="h-3 w-3" /> {c.title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5 flex-wrap">
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#002FA7]">
                      <Mail className="h-3 w-3" /> {c.email}
                    </a>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#002FA7]">
                        <Phone className="h-3 w-3" /> {c.phone}
                      </a>
                    )}
                    {c.has_password && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700"
                        title={c.password_set_at
                          ? `Password set on ${new Date(c.password_set_at).toLocaleString()}`
                          : "Password is set"}
                        data-testid={`contact-pw-set-${c.user_id}`}>
                        <ShieldCheck className="h-3 w-3" />
                        Password set
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <Button size="sm" variant="ghost" className="rounded-sm text-xs"
                disabled={settingPwId === c.user_id}
                onClick={() => adminResetPassword(c)}
                title="Generate a new strong password for this contact — shown once."
                data-testid={`contact-admin-set-pw-${c.user_id}`}>
                {settingPwId === c.user_id
                  ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <KeyRound className="h-3.5 w-3.5 mr-1" />}
                {settingPwId === c.user_id ? "Resetting…" : "Reset password"}
              </Button>

              <Button size="sm" variant="ghost" className="rounded-sm text-xs"
                disabled={resendingId === c.user_id}
                onClick={() => resendWelcome(c)}
                data-testid={`contact-resend-welcome-${c.user_id}`}>
                <Send className="h-3.5 w-3.5 mr-1" />
                {resendingId === c.user_id ? "Sending…" : "Welcome email"}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-sm text-xs"
                onClick={() => generateResetLink(c)}
                data-testid={`contact-reset-pw-${c.user_id}`}>
                <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset link
              </Button>
              <Button size="sm" variant="ghost" className="rounded-sm"
                onClick={() => beginEdit(c)}
                data-testid={`contact-edit-${c.user_id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                    data-testid={`contact-delete-${c.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-sm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {c.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      They&apos;ll lose access to the client portal immediately and won&apos;t
                      appear on future PDF reports.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                    <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                      onClick={() => remove(c)}
                      data-testid={`contact-confirm-delete-${c.user_id}`}>
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        ))}
        {contacts.length === 0 && !adding && (
          <Card className="rounded-sm border-dashed border-zinc-300">
            <CardContent className="p-10 text-center text-sm text-zinc-500">
              No contacts yet — click <strong>Invite contact</strong> above to add one.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Iceberg Digital team reps assigned to this client */}
      <AgencyRepsSection clientId={clientId} />

      {/* Password reset LINK dialog — admin-triggered, contact sets own password.
          Re-used by the "Resend welcome email" flow when SendGrid isn't wired:
          we surface both the login URL and reset URL so the admin can copy-
          paste them into an email manually. */}
      <AlertDialog open={!!resetLinkInfo} onOpenChange={(o) => !o && setResetLinkInfo(null)}>
        <AlertDialogContent className="rounded-sm max-w-lg" data-testid="reset-link-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resetLinkInfo?.headline || "Password reset link generated"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Send the link{resetLinkInfo?.loginUrl ? "s" : ""} below to{" "}
              <strong>{resetLinkInfo?.name || resetLinkInfo?.email}</strong>.
              The contact opens the reset link and sets their own password, then
              signs in via the login URL.
              {resetLinkInfo?.expires_at && (
                <> The reset link expires on <strong>{new Date(resetLinkInfo.expires_at).toLocaleString()}</strong>.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {resetLinkInfo?.loginUrl && (
            <div className="space-y-1.5">
              <Label className="label-overline">Login URL</Label>
              <div className="flex gap-2">
                <Input value={resetLinkInfo.loginUrl} readOnly
                  className="rounded-sm font-mono text-xs"
                  data-testid="welcome-login-url" />
                <Button onClick={async () => {
                  const ok = await copyToClipboard(resetLinkInfo.loginUrl);
                  toast[ok ? "success" : "error"](ok ? "Login URL copied" : "Couldn't copy");
                }} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="welcome-login-copy">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            {resetLinkInfo?.loginUrl && <Label className="label-overline">Reset link</Label>}
            <div className="flex gap-2">
              <Input value={resetLinkInfo?.url || ""} readOnly
                className="rounded-sm font-mono text-xs"
                data-testid="reset-link-url" />
              <Button onClick={async () => {
                const ok = await copyToClipboard(resetLinkInfo?.url || "");
                toast[ok ? "success" : "error"](ok ? "Link copied" : "Couldn't copy — select the URL manually");
              }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="reset-link-copy">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogAction className="rounded-sm" onClick={() => setResetLinkInfo(null)}>
              Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin-set password reveal dialog — plaintext shown ONCE. After
          closing, the password is gone from the UI forever; only the
          bcrypt hash lives on the user record. */}
      <AlertDialog open={!!setPwInfo} onOpenChange={(o) => !o && setSetPwInfo(null)}>
        <AlertDialogContent className="rounded-sm max-w-lg" data-testid="admin-set-pw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              New password for {setPwInfo?.name || setPwInfo?.email}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Copy this password now and pass it to the contact via a secure
              channel (e.g. password manager share, signal, in-person).
              <strong className="block mt-1.5 text-red-600">
                Once you close this dialog the password cannot be shown again —
                you&apos;ll need to reset it to a brand-new value.
              </strong>
              Their existing sessions have been signed out and any pending
              password-reset links have been invalidated.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-1.5">
            <Label className="label-overline">Email (username)</Label>
            <Input value={setPwInfo?.email || ""} readOnly
              className="rounded-sm font-mono text-xs"
              data-testid="admin-set-pw-email" />
          </div>

          <div className="space-y-1.5">
            <Label className="label-overline">Password (shown once)</Label>
            <div className="flex gap-2">
              <Input value={setPwInfo?.password || ""} readOnly
                className="rounded-sm font-mono text-sm tracking-wider"
                data-testid="admin-set-pw-value" />
              <Button onClick={async () => {
                const ok = await copyToClipboard(setPwInfo?.password || "");
                toast[ok ? "success" : "error"](
                  ok ? "Password copied to clipboard" : "Couldn't copy — select manually");
              }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="admin-set-pw-copy">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500 flex items-center gap-1 mt-1">
              <EyeOff className="h-3 w-3" />
              We do not store the plaintext — only a one-way hash.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogAction className="rounded-sm" onClick={() => setSetPwInfo(null)}
              data-testid="admin-set-pw-done">
              I&apos;ve copied it · Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function emptyDraft() {
  return { email: "", first_name: "", last_name: "", password: "", title: "", phone: "", is_primary: false, can_login: true };
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-overline">
        {label} {required && <span className="text-red-600 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
